import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/ui/dialog';
import PanoramaViewer from './PanoramaViewer';
import { Tabs, LayoutsView, HotspotsView, MarkerContextMenu, ChangeSequenceDialog, AdjustSequenceWidget } from './components';
import { MarkerToolbar } from './components/MarkerToolbar';
import { AreaFormDialog } from './components/AreaFormDialog';
import { PhotoSelectionTooltip } from './components/PhotoSelectionTooltip';
import { useInteractiveViewer } from './hooks/useInteractiveViewer';
import { useInteractionHandlers } from './hooks/useInteractionHandlers';
import { Position } from './types/types';
import './styles/HotspotStyles.css';
import { BaseLayout } from '@/components/layout';

const InteractiveViewerRefactored: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const lastAppliedDeepLinkRef = useRef<string>('');
  const [pendingHotspotId, setPendingHotspotId] = useState<number | null>(null);
  
  // Detect layout type from current URL path
  const layoutType = React.useMemo(() => {
    const path = location.pathname;
    if (path.includes('/progress')) {
      return 'Progress';
    } else if (path.includes('/asbuilds')) {
      return 'asbuilds'; // This matches the backend logic for Asbuild + Corridor
    }
    return undefined;
  }, [location.pathname]);

  const {
    state,
    actions,
    refs,
    data,
    mutations,
  } = useInteractiveViewer(layoutType);

  const deepLinkParams = useMemo(() => {
    const parseNumber = (value: string | null): number | undefined => {
      if (!value) return undefined;
      const n = Number(value);
      return Number.isFinite(n) ? n : undefined;
    };

    const layoutId = searchParams.get('layout') ?? undefined;
    const photoId = parseNumber(searchParams.get('photo'));
    const hotspotId = parseNumber(searchParams.get('hotspot'));
    const pitch = parseNumber(searchParams.get('pitch'));
    const yaw = parseNumber(searchParams.get('yaw'));
    const coordinates = pitch !== undefined && yaw !== undefined ? { pitch, yaw } : undefined;

    return {
      key: searchParams.toString(),
      layoutId,
      photoId,
      hotspotId,
      coordinates,
    };
  }, [searchParams]);

  const addMeasurementPoint = useCallback((point: Position) => {
    const adjustedPoint = { ...point };

    if (state.currentPolygon.length > 0) {
      const lastPoint = state.currentPolygon[state.currentPolygon.length - 1];

      if (state.isHorizontalConstrained) {
        adjustedPoint.y = lastPoint[1];
      } else if (state.isVerticalConstrained) {
        adjustedPoint.x = lastPoint[0];
      }
    }

    actions.setCurrentPolygon([...state.currentPolygon, [adjustedPoint.x, adjustedPoint.y]]);
  }, [state.currentPolygon, state.isHorizontalConstrained, state.isVerticalConstrained, actions]);

  const interactionHandlers = useInteractionHandlers({
    mode: state.mode,
    photoIndexMode: state.photoIndexMode,
    isDragging: state.isDragging,
    scale: state.scale,
    position: state.position,
    displayPhotoMarkers: data.displayPhotoMarkers,
    svgRef: refs.svgRef,
    groupRef: refs.groupRef,
    dragStartRef: refs.dragStartRef,
    lastDragPointRef: refs.lastDragPointRef,
    dragStartTimeRef: refs.dragStartTimeRef,
    touchStartDistanceRef: refs.touchStartDistanceRef,
    touchStartScaleRef: refs.touchStartScaleRef,
    setIsDragging: (dragging) => actions.setIsDragging(dragging),
    setPosition: actions.setPosition,
    setScale: actions.setScale,
    setSelectedMarkers: actions.setSelectedMarkers,
    setLastSelectedMarkerId: actions.setLastSelectedMarkerId,
    setIsHorizontalConstrained: actions.setIsHorizontalConstrained,
    setIsVerticalConstrained: actions.setIsVerticalConstrained,
    addMeasurementPoint,
    addPhotoMarker: actions.addPhotoMarker,
    deleteSelectedMarkers: actions.deleteSelectedMarkers,
    copySelectedMarkers: actions.copySelectedMarkers,
    pasteMarkers: actions.pasteMarkers,
    undo: actions.undo,
    onUpdateMarkerAttributes: actions.setMarkerAttributes,
  });

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (state.photoSelectionTooltip.visible) {
        const target = e.target as HTMLElement;
        if (!target.closest('.photo-selection-tooltip')) {
          actions.setPhotoSelectionTooltip({ ...state.photoSelectionTooltip, visible: false });
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [state.photoSelectionTooltip.visible, actions]);

  const handlePhotoSelectionTooltipSelect = useCallback((photoId: number, photoIndex: number) => {
    actions.setCurrentPhotoIndex(photoIndex);
    actions.setOpenedPhotoId(photoId);
    actions.showPanorama(photoId);
  }, [actions]);

  const handlePhotoSelectionTooltipClose = useCallback(() => {
    actions.setPhotoSelectionTooltip({ ...state.photoSelectionTooltip, visible: false });
  }, [state.photoSelectionTooltip, actions]);

  // Apply URL deep-link params once per unique query string.
  useEffect(() => {
    if (!deepLinkParams.key || deepLinkParams.key === lastAppliedDeepLinkRef.current) return;

    // If a layout id was provided, wait until layouts are loaded before applying.
    if (deepLinkParams.layoutId && data.layouts.length === 0) return;

    let resolvedLayoutId: string | undefined;
    if (deepLinkParams.layoutId) {
      const foundLayout = data.layouts.find((l) => String(l.id) === String(deepLinkParams.layoutId));
      resolvedLayoutId = foundLayout ? String(foundLayout.id) : undefined;
    }

    actions.openDeepLinkTarget({
      layoutId: resolvedLayoutId,
      photoId: deepLinkParams.photoId,
      hotspotId: deepLinkParams.hotspotId,
      coordinates: deepLinkParams.coordinates,
    });

    if (deepLinkParams.hotspotId && !deepLinkParams.coordinates) {
      setPendingHotspotId(deepLinkParams.hotspotId);
    } else {
      setPendingHotspotId(null);
    }

    lastAppliedDeepLinkRef.current = deepLinkParams.key;
  }, [deepLinkParams, data.layouts, actions]);

  // Fallback: if hotspot id is provided but pitch/yaw are not, resolve coords from loaded hotspots.
  useEffect(() => {
    if (!pendingHotspotId || !data.hotspotsData?.hotspots) return;

    const hotspot = data.hotspotsData.hotspots.find((h) => h.id === pendingHotspotId);
    if (hotspot) {
      actions.setTargetCoordinates({ pitch: hotspot.pitch - 5, yaw: hotspot.yaw });
      setPendingHotspotId(null);
    }
  }, [pendingHotspotId, data.hotspotsData, actions]);

  const tabs = [
    {
      id: 'layouts',
      label: 'Layouts',
      content: (
        <LayoutsView
          selectedLayoutId={state.selectedLayoutId}
          setSelectedLayoutId={actions.setSelectedLayoutId}
          layouts={data.layouts}
          loadLayout={actions.loadLayout}
          layoutsLoading={data.layoutsLoading}
          layoutDataLoading={data.layoutDataLoading}
          imagePath={state.imagePath}
          pdfPath={state.pdfPath}
          pdfPageNumber={state.pdfPageNumber}
          pdfCropRegion={state.pdfCropRegion}
          isPdfLayout={state.isPdfLayout}
          imageDimensions={state.imageDimensions}
                     areaTags={data.areaTags}
           projectId={projectId!}
           displayPhotoMarkers={data.displayPhotoMarkers}
          getCursor={actions.getCursor}
          handleMouseDown={interactionHandlers.handleMouseDown}
          handleSvgClick={interactionHandlers.handleSvgClick}
          handleUnitClick={actions.handleUnitClick}
          handleMarkerClick={actions.handleMarkerClick}
          deletePhotoMarker={actions.deletePhotoMarker}
          handleMarkerRightClick={actions.handleMarkerRightClick}
          handleMarkerDragStart={actions.handleMarkerDragStart}
          onWheelZoom={interactionHandlers.handleWheelZoom}
          handleTouchStart={interactionHandlers.handleTouchStart}
          handleTouchMove={interactionHandlers.handleTouchMove}
          handleTouchEnd={interactionHandlers.handleTouchEnd}
          zoom={actions.zoom}
          reset={actions.reset}
          mode={state.mode}
          setMode={actions.handleSetMode}
          photoIndexMode={state.photoIndexMode}
          setCurrentPolygon={actions.setCurrentPolygon}
          closePolygon={actions.closePolygon}
          clearPolygon={actions.clearPolygon}
          savePhotoMarkers={actions.savePhotoMarkers}
          setPhotoIndexMode={actions.setPhotoIndexMode}
          setSelectedMarkers={actions.setSelectedMarkers}
          setShowMarkerToolbar={actions.setShowMarkerToolbar}
          setSelectedUnit={actions.setSelectedUnit}
          setImageDimensions={actions.setImageDimensions}
          addPhotoMarker={actions.addPhotoMarker}
          addMeasurementPoint={addMeasurementPoint}
          svgRef={refs.svgRef}
          groupRef={refs.groupRef}
          position={state.position}
          scale={state.scale}
          setPosition={actions.setPosition}
          setScale={actions.setScale}
          selectedUnit={state.selectedUnit}
          currentPolygon={state.currentPolygon}
          selectedMarkers={state.selectedMarkers}
          showCoordinates={state.showCoordinates}
          savePhotoMarkersMutation={mutations.savePhotoMarkersMutation}
          isHorizontalConstrained={state.isHorizontalConstrained}
          isVerticalConstrained={state.isVerticalConstrained}
          setShowCoordinates={actions.setShowCoordinates}
        />
      ),
    },
    ...(projectId
      ? [
          {
            id: 'hotspots',
            label: 'Hotspots',
            content: <HotspotsView projectId={projectId!} handleViewHotspot={actions.handleViewHotspot} />,
          },
        ]
      : []),
  ];

  return (
    <BaseLayout fullHeight>
    <div className="h-full w-full bg-background flex flex-col overflow-hidden">
      {/* Tabs component that properly handles its own layout */}
      <Tabs tabs={tabs} defaultTab="layouts" />

      {/* Photo Carousel Modal */}
      <Dialog
        open={state.showPhotoCarousel}
        onOpenChange={actions.setShowPhotoCarousel}
      >
        <DialogHeader>
          <DialogTitle className="hidden">Photo Carousel</DialogTitle>
          <DialogDescription className="hidden">
            Select a photo to view in the carousel.
          </DialogDescription>
        </DialogHeader>
        <DialogContent className="max-w-6xl w-full h-[90vh] p-0">
          {state.openedPhotoId && data.photoData?.image_url ? (
            <PanoramaViewer
              photoId={state.openedPhotoId}
              imageUrl={data.photoData.image_url}
              photoData={data.photoData}
              hotspotsData={data.hotspotsData}
              projectId={projectId}
              // Enable features
              enableFAB={true}
              enableHotspots={true}
              enablePhotoNav={true}
              enableDateNav={true}
              // Callbacks
              onNavigateToPhotoId={actions.navigateToMarkerPhotoById}
              onHotspotCreated={actions.refreshHotspots}
              // Share feature - layout context for location preview
              markerNumber={
                data.displayPhotoMarkers.find((m) =>
                  m.photos.some((p) => p.id === state.openedPhotoId)
                )?.number
              }
              layoutImageUrl={state.imagePath || undefined}
              layoutImageDimensions={
                state.imageDimensions.width && state.imageDimensions.height
                  ? state.imageDimensions
                  : undefined
              }
              photoMarkers={data.displayPhotoMarkers}
              targetCoordinates={state.targetCoordinates}
              onTargetReached={() => actions.setTargetCoordinates(null)}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-white bg-black">
              Loading panorama...
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Area Form Modal */}
      <AreaFormDialog
        open={state.showAreaForm}
        areaName={state.areaName}
        selectedPhotos={state.selectedPhotos}
        availablePhotos={data.availablePhotos}
        onOpenChange={actions.setShowAreaForm}
        onAreaNameChange={actions.setAreaName}
        onSelectedPhotosChange={actions.setSelectedPhotos}
        onSave={actions.saveArea}
        onCancel={() => {
          actions.setShowAreaForm(false);
          actions.setAreaName('');
          actions.setSelectedPhotos([]);
        }}
      />

      {/* Marker Toolbar */}
      <MarkerToolbar
        visible={state.showMarkerToolbar && state.photoIndexMode}
        currentMarkerNumber={state.currentMarkerNumber}
        markerAttributes={state.markerAttributes}
        maintainAspectRatio={state.maintainAspectRatio}
        photoMarkers={data.photoMarkers}
        selectedMarkers={state.selectedMarkers}
        onMarkerNumberChange={actions.setCurrentMarkerNumber}
        onMarkerAttributesChange={actions.setMarkerAttributes}
        onMaintainAspectRatioChange={actions.setMaintainAspectRatio}
        onUpdateSelectedMarkers={actions.updateSelectedMarkers}
        onResetSequence={() => {
          // Reset sequence logic would be implemented here
        }}
      />

      {/* Photo Selection Tooltip */}
      <PhotoSelectionTooltip
        tooltip={state.photoSelectionTooltip}
        onPhotoSelect={handlePhotoSelectionTooltipSelect}
        onClose={handlePhotoSelectionTooltipClose}
      />

      {/* Marker Context Menu */}
      <MarkerContextMenu
        visible={state.contextMenu.visible}
        position={state.contextMenu.position}
        selectedCount={state.selectedMarkers.size}
        selectedMarkerId={Array.from(state.selectedMarkers)[0]}
        onDelete={actions.deleteSelectedMarkers}
        onChangeSequence={actions.openSequenceChangeDialog}
        onAdjustSequence={() => actions.setAdjustSequenceMode(true, state.contextMenu.position)}
        onClose={actions.closeContextMenu}
      />

      {/* Change Sequence Dialog */}
      <ChangeSequenceDialog
        open={state.sequenceChangeDialog.visible}
        currentNumber={
          state.sequenceChangeDialog.markerId
            ? data.displayPhotoMarkers.find(m => m.id === state.sequenceChangeDialog.markerId)?.number || 1
            : 1
        }
        maxNumber={data.displayPhotoMarkers.length}
        onConfirm={actions.changeMarkerSequence}
        onCancel={actions.closeSequenceChangeDialog}
      />

      {/* Adjust Sequence Widget */}
      <AdjustSequenceWidget
        visible={state.adjustSequenceMode && state.selectedMarkers.size > 0}
        selectedCount={state.selectedMarkers.size}
        position={state.adjustSequencePosition || undefined}
        onIncrement={actions.incrementSelectedMarkers}
        onDecrement={actions.decrementSelectedMarkers}
        onClose={() => actions.setAdjustSequenceMode(false)}
      />
    </div>
    </BaseLayout>
  );
};

export default InteractiveViewerRefactored; 